import { Container } from "aurelia-dependency-injection";
import { defineComponent, PropType } from "vue";

import { ITypeaheadOptions } from "../../../../../../resources-vue/vue-interfaces/i-typeahead";
import { IFamilyHealthHistory } from "../../../../../../resources-vue/vue-interfaces/i-health";
import { FamilyHealthHistoryModel } from "../../../../../../models/pal-health-history/family-health-history-model";
import { PatientRelations } from "../../../../../../models/pal-health-history/patient-relations";
import { RelationStatus } from "../../../../../../enums/RelationStatusEnum";
import { enumToMap } from "../../../../../../common/vue-helpers/modifiers/enum-modifiers";
import { PatientService } from "../../../../../../services/patient.service";
import { ToastrService } from "../../../../../../services/toastr.service";
import { PatientRelationship } from "../../../../../../enums/PatientRelationshipsEnum";
import { ApplicationEnum } from "../../../../../../enums/ApplicationEnum";

import TypeaheadInput from "../../../../../../resources-vue/vue-custom-elements/TypeaheadInput/TypeaheadInput.vue";
import TablePrompt from "../../../../../../resources-vue/vue-custom-elements/TablePrompt/TablePrompt.vue";
import FamilyHistoryForm from "../FamilyHealthHistoryForm/FamilyHealthHistoryForm.vue";
import EmptyTextHandler from "../../../../../../resources-vue/vue-custom-elements/EmptyTextHandler/EmptyTextHandler.vue";

export default defineComponent({
    components: {
        TablePrompt,
        FamilyHistoryForm,
        EmptyTextHandler
    },

    props: {
        familyHealthHistory: {
            type: Object as PropType<IFamilyHealthHistory>,
            default: null
        },

        patientId: {
            type: String,
            default: ""
        },
        columns: { type: Number },
        pageReset: {
            type: Function
        },
        currentEditRowId: {
            type: String
        },
        accountId: {
            type: String
        }
    },
    data() {
        return {
            _patientsService: null as PatientService,
            _toastrService: null as ToastrService,
            selectedRelationship: [] as number[],
            relationshipOptions: [] as ITypeaheadOptions[],
            typeaheadInstance: TypeaheadInput,
            selectedHealthCondition: null,
            patientRelationshipEnum: Array.from(PatientRelationship.enumWithDescriptions.values()),
            relationStatusEnum: Array.from(RelationStatus.enumWithDescriptions.values()),
            enumToMap,
            isEditRequested: false as boolean,
            isDeleteRequested: false as boolean,
            isDeleteInProgress: false as boolean,
            isEditInProgress: false as boolean,
            isViewDetailsRequested: false as boolean,
            cacheFamilyHeathHistory: null as FamilyHealthHistoryModel,
            PatientRelationship,
            RelationStatus
        };
    },

    emits: ["update:currentEditRowId"],

    watch: {
        currentEditRowId() {
            this.currentEditRowIdChanged();
        }
    },

    created() {
        this._patientsService = Container.instance.get(PatientService);
        this._toastrService = Container.instance.get(ToastrService);
    },

    mounted() {
        if (!!this.familyHealthHistory.ConditionCode) {
            this.selectedHealthCondition = {
                name: this.familyHealthHistory.Condition,
                value: this.familyHealthHistory.ConditionCode
            };
        } else {
            this.selectedHealthCondition = null;
        }
    },

    methods: {
        getRelationship() {
            if (!this.familyHealthHistory?.PatientRelations?.length) {
                return "";
            }
            let relationships = this.familyHealthHistory.PatientRelations.map((relationship: any) =>
                PatientRelationship.getKeyUsingValue(relationship.RelationshipToPatient)
            );
            return relationships.join(", ");
        },

        getRelationshipStatuses() {
            if (!this.familyHealthHistory?.PatientRelations?.length) {
                return "";
            }
            let relationshipStatuses = this.familyHealthHistory.PatientRelations.map((relationship: any) => {
                if (!relationship?.Status) {
                    return "N/A";
                }
                return RelationStatus.getKeyUsingValue(relationship.Status);
            });
            return relationshipStatuses.join(", ");
        },

        getAgeOfDiagnosis() {
            if (!this.familyHealthHistory?.PatientRelations?.length) {
                return "";
            }
            let ages = this.familyHealthHistory.PatientRelations.map((relationship: any) => {
                if (!!relationship?.Age) {
                    return relationship.Age;
                }
                return "N/A";
            });
            return ages.join(", ");
        },

        openEditFamilyHistory() {
            let cacheFamilyHistory = JSON.parse(JSON.stringify(this.familyHealthHistory));
            cacheFamilyHistory.PatientRelations = [];
            this.familyHealthHistory?.PatientRelations?.forEach((relation: any) => {
                cacheFamilyHistory.PatientRelations.push(new PatientRelations(relation));
            });
            this.cacheFamilyHeathHistory = new FamilyHealthHistoryModel(cacheFamilyHistory);
            this.isEditRequested = true;
            this.$emit("update:currentEditRowId", this.familyHealthHistory.Id);
        },

        async editFamilyHistory() {
            const isValidForm = await (this.$refs.editFamilyHistoryForm as any).v$.$validate();
            if (isValidForm) {
                try {
                    this.isEditInProgress = true;
                    const payload = {
                        Application: ApplicationEnum.AxxessPalliative,
                        AccountId: this.accountId,
                        PalliativePatientFamilyHealthHistory: {
                            ...this.cacheFamilyHeathHistory
                        }
                    };
                    await this._patientsService.updateFamilyHealthHistory(this.patientId, payload);
                    this.cancelEditFamilyHistory();
                    this.pageReset();
                    this._toastrService.success({
                        title: "Family Health History Updated",
                        message: "Family Health History has been successfully updated."
                    });
                } catch (err) {
                    console.error(err);
                    this._toastrService.error({
                        title: "Error - Family Health History",
                        message: "There was an error updating Family Health History. Please try again."
                    });
                } finally {
                    this.isEditInProgress = false;
                }
            } else {
                this._toastrService.error({
                    title: "Error - Family Health History",
                    message: "Please fill required fields"
                });
            }
        },

        cancelEditFamilyHistory() {
            this.isEditRequested = false;
            this.cacheFamilyHeathHistory = null;
            this.$emit("update:currentEditRowId", "");
        },

        openViewDetails() {
            this.$emit("update:currentEditRowId", this.familyHealthHistory.Id);
            this.isViewDetailsRequested = true;
        },

        closeViewDetails() {
            this.$emit("update:currentEditRowId", "");
            this.isViewDetailsRequested = false;
        },

        openDeleteRequest() {
            this.isDeleteRequested = true;
        },

        async deleteFamilyHistory() {
            try {
                this.isDeleteInProgress = true;
                const query = {
                    application: ApplicationEnum.AxxessPalliative,
                    accountId: this.accountId
                };
                await this._patientsService.deleteFamilyHealthHistory(
                    this.patientId,
                    this.familyHealthHistory.Id,
                    query
                );
                this.pageReset();
                this._toastrService.success({
                    title: "Family Health History Deleted",
                    message: "Family Health History has been successfully deleted."
                });
            } catch (err) {
                console.error(err);
                this._toastrService.error({
                    title: "Error - Family Health History",
                    message: "There was an error deleting Family Health History. Please try again."
                });
            } finally {
                this.isDeleteInProgress = false;
            }
        },

        closeDeleteRequest() {
            this.isDeleteRequested = false;
        },

        currentEditRowIdChanged() {
            if (this.currentEditRowId !== this.familyHealthHistory.Id) {
                this.isEditRequested = false;
                this.isViewDetailsRequested = false;
                this.isDeleteRequested = false;
            }
        }
    }
});
